import { assert_array_has_1_item, assert_instanceof } from '@shared/asserts/utils_asserts';
import { CONSTANTS_GENERATION__value_range as CONSTANTS_RANGE } from '@shared/constants/CONSTANTS_GENERATION__value_range';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { ENUM__dto_class_name } from '@shared/serialization/ENUM__dto_class_name';
import { ENUM__field_graph_interaction } from '../../_fields/_base/_enums/ENUM__field_graph_interaction';
import { ENUM__field_store_interaction } from '../../_fields/_base/_enums/ENUM__field_store_interaction';
import { FIELD_PRIMITIVE__int } from '../../_fields/_primitives/number/FIELD_PRIMITIVE__int';
import { ENUM__image_asset_category } from '../../_fields/ENUM__image_asset_category';
import { ENUM__image_transform } from '../../_fields/image/_enums/ENUM__image_transform';
import { FIELD__image_asset } from '../../_fields/image/FIELD__image_asset';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { DTO_META__invocation_info } from '../_base/DTO_META__invocation_info';
import { ENUM__field_role } from '../_base/ENUM__field_role';
import { ENUM__invocation_role } from '../_base/ENUM__invocation_role';
import { ENUM__invocation_type } from '../_base/ENUM__invocation_type';
import { INVOCATION__base } from '../_base/INVOCATION__base';

const { IMAGE_SIZE_ASPECT_RATIO } = CONSTANTS_RANGE;

//todo: move to constants
export const IMAGE_SIZE_PROPERTY = FIELD_PRIMITIVE__int.partial({
  value: 1,
  multiple_of: 1,
  //
  value_range: IMAGE_SIZE_ASPECT_RATIO,
  values_allowed: IMAGE_SIZE_ASPECT_RATIO,
  //
  slider_step: 1,
  slider_step_values: [1],
});

export class INVOCATION_GRID2D__view extends INVOCATION__base {
  /*
   * STATIC FIELDS
   */
  static _class_name_static: string = ENUM__dto_class_name.INVOCATION_GRID2D__VIEW;

  /*
   * STATIC METHODS
   */

  static override create_node_for<T extends INVOCATION__base>(
    this: new () => T,
    graph: DTO_GRAPH__instance,
    node_role: ENUM__invocation_role = ENUM__invocation_role.GRID2D__VIEW,
  ): T {
    return super.create_node_for.call(this, graph, node_role) as T;
  }

  /*
   * STATIC METHODS
   */

  static create_default_meta(): DTO_META__invocation_info {
    let out = super.create_default_meta();

    out.is_deletable = false;

    return out;
  }

  /*
   * META FIELDS
   */
  aspect_ratio_reference_size_original!: DTO__image_size;

  /*
   * FIELDS
   */

  /*
   * CELL SIZE
   */
  @FIELD_PRIMITIVE__int.init_as_input(IMAGE_SIZE_PROPERTY.clone(), {
    grid2d__can_be_dimension: false,
    generation__dynamic_duration: true,
    field_role: ENUM__field_role.IMAGE__OUT_AR_WIDTH,
    store__interaction: ENUM__field_store_interaction.READ_AND_WRITE,
  })
  cell__aspect_ratio_width!: FIELD_PRIMITIVE__int;

  @FIELD_PRIMITIVE__int.init_as_input(IMAGE_SIZE_PROPERTY.clone(), {
    grid2d__can_be_dimension: false,
    generation__dynamic_duration: true,
    field_role: ENUM__field_role.IMAGE__OUT_AR_HEIGHT,
    store__interaction: ENUM__field_store_interaction.READ_AND_WRITE,
  })
  cell__aspect_ratio_height!: FIELD_PRIMITIVE__int;

  @FIELD__image_asset.init_as_input_transform_none(
    { category: ENUM__image_asset_category.IMAGE },
    {
      graph__interaction: ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE,
      field_role: ENUM__field_role.IMAGE__OUT_AR_REFERENCE,
      store__interaction: ENUM__field_store_interaction.READ_AND_WRITE,
      generation__has_effect_static: false,
      data__is_required: false,
    },
  )
  cell__aspect_ratio_reference!: FIELD__image_asset;

  /*
   * OUTPUT
   */

  @FIELD__image_asset.init_as_input(
    {
      category: ENUM__image_asset_category.ANY,
      preprocessing_transform__current: ENUM__image_transform.FILL,
      preprocessing_transform__allowed: [ENUM__image_transform.FILL],
    },
    { ui__is_visible: false },
  )
  image!: FIELD__image_asset;

  /*
   * STUBS
   */
  @FIELD_PRIMITIVE__int.init_as_input(
    { value: 0, value_range: CONSTANTS_RANGE.ZERO, values_allowed: CONSTANTS_RANGE.ZERO_TO_ONE },
    { ui__is_readonly: true, ui__has_component: false, generation__has_effect_static: false, data__is_required: false },
  )
  stub_grid2d_column!: FIELD_PRIMITIVE__int;

  @FIELD_PRIMITIVE__int.init_as_input(
    { value: 0, value_range: CONSTANTS_RANGE.ZERO, values_allowed: CONSTANTS_RANGE.ZERO_TO_ONE },
    { ui__is_readonly: true, ui__has_component: false, generation__has_effect_static: false, data__is_required: false },
  )
  stub_grid2d_row!: FIELD_PRIMITIVE__int;

  /*
   *
   */
  @FIELD__image_asset.init_as_output(
    { category: ENUM__image_asset_category.ANY },
    {
      ui__is_visible: false,
      field_role: '🖼️ Grid2d Image',
    },
  )
  image_out!: FIELD__image_asset;

  /*
   * PROPERTIES
   */

  get grid2d_cell__aspect_ratio() {
    let out = this.cell__aspect_ratio_width.get_value() / this.cell__aspect_ratio_height.get_value();

    out = out.to_fixed_floor(2);

    return out;
  }

  set_aspect_ratio(aspect_ratio: number) {
    let [best_w, best_h] = [1, 1];

    let error_smallest = Infinity;

    for (let h = IMAGE_SIZE_ASPECT_RATIO.min; h <= IMAGE_SIZE_ASPECT_RATIO.max; h++) {
      let w = Math.round(h * aspect_ratio);

      if (w >= IMAGE_SIZE_ASPECT_RATIO.min && w <= IMAGE_SIZE_ASPECT_RATIO.max) {
        let current_ratio = w / h;
        let error = Math.abs(current_ratio - aspect_ratio);

        if (error < error_smallest) {
          error_smallest = error;
          [best_w, best_h] = [w, h];
        }
      }
    }

    this.cell__aspect_ratio_width.set_value(best_w);
    this.cell__aspect_ratio_width.meta.ui__is_readonly = false;

    this.cell__aspect_ratio_height.set_value(best_h);
    this.cell__aspect_ratio_height.meta.ui__is_readonly = false;

    this.cell__aspect_ratio_reference.image__clear();
  }

  /*
   * EVENTS
   */

  /*
   * METHODS
   */

  constructor() {
    super(ENUM__invocation_type.GRID2D__VIEW);

    this.aspect_ratio_reference_size_original = DTO__image_size.CREATE_STUB();
  }

  post_init() {
    super.post_init();

    this.meta.node_role = ENUM__invocation_role.GRID2D__VIEW;
  }

  prepare_for_usage_in(graph: DTO_GRAPH__instance) {
    super.prepare_for_usage_in(graph);

    /*
     * IMAGE CATEGORY
     */

    let edges = graph.edges__get_to([this.image]);

    if (edges.is_empty()) {
      this.image.category = ENUM__image_asset_category.ANY;
      this.image_out.category = ENUM__image_asset_category.ANY;
    } else {
      let edge = assert_array_has_1_item(edges);
      let field_from = graph.field__get_by_id(edge.field_from__field_id);
      assert_instanceof(field_from, FIELD__image_asset);
      this.image.category = field_from.category;
      this.image_out.category = field_from.category;
    }

    /*
     * CALCULATE ASPECT RATIO FROM REFERENCE
     */

    if (this.cell__aspect_ratio_reference.get_has_content()) {
      this.set_aspect_ratio_from_image();

      this.cell__aspect_ratio_width.meta.ui__is_readonly = true;
      this.cell__aspect_ratio_height.meta.ui__is_readonly = true;
    } else {
      this.aspect_ratio_reference_size_original = DTO__image_size.CREATE_STUB();

      this.cell__aspect_ratio_width.meta.ui__is_readonly = false;
      this.cell__aspect_ratio_height.meta.ui__is_readonly = false;
    }
  }

  set_aspect_ratio_from_image() {
    let image_size = this.cell__aspect_ratio_reference.get_image_size();

    if (this.aspect_ratio_reference_size_original.is_equal(image_size)) return; //already setted
    this.aspect_ratio_reference_size_original = image_size.clone();

    /*
     *
     */

    let { width, height } = image_size;

    // 1. Find the Greatest Common Divisor (GCD) to simplify the fraction
    const gcd_func = (a: number, b: number): number => (b === 0 ? a : gcd_func(b, a % b));
    const gcd = gcd_func(width, height);

    let [ratio_w, ratio_h] = [Math.round(width / gcd), Math.round(height / gcd)];

    // 2. If either ratio exceeds 16, scale them down proportionally
    if (ratio_w > IMAGE_SIZE_ASPECT_RATIO.max || ratio_h > IMAGE_SIZE_ASPECT_RATIO.max) {
      const maxVal = Math.max(ratio_w, ratio_h);
      ratio_w = Math.round((ratio_w / maxVal) * IMAGE_SIZE_ASPECT_RATIO.max);
      ratio_h = Math.round((ratio_h / maxVal) * IMAGE_SIZE_ASPECT_RATIO.max);
    }

    // 3. Ensure bounds are strictly between right range
    ratio_w = Math.max(IMAGE_SIZE_ASPECT_RATIO.min, Math.min(IMAGE_SIZE_ASPECT_RATIO.max, ratio_w));
    ratio_h = Math.max(IMAGE_SIZE_ASPECT_RATIO.min, Math.min(IMAGE_SIZE_ASPECT_RATIO.max, ratio_h));

    this.cell__aspect_ratio_width.set_value(ratio_w);
    this.cell__aspect_ratio_height.set_value(ratio_h);
  }

  /*
   *
   */

  fields_ids__optional(): string[] {
    return [this.cell__aspect_ratio_reference.meta.field_id];
  }
}
